import { alpha } from "@mui/material/styles";

/** Hero superior (sticky opcional) — reutilizable en pantallas admin */
export const pageHeroShellSx = (theme, { sticky = false } = {}) => ({
  ...(sticky ? { position: "sticky", top: 0, zIndex: 20 } : {}),
  mb: 1.5,
  borderRadius: 3,
  overflow: "hidden",
  border: "1px solid",
  borderColor: alpha(theme.palette.divider, 0.85),
  background:
    theme.palette.mode === "dark"
      ? `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.24)} 0%, ${alpha(theme.palette.background.paper, 0.96)} 55%)`
      : "linear-gradient(145deg, #e8f6f8 0%, #f4f0ff 48%, #f5faf7 100%)",
  backdropFilter: "blur(12px)",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 4px 28px rgba(0,0,0,0.42)"
      : "0 8px 32px rgba(15, 23, 42, 0.07)",
  "&::before": {
    content: '""',
    display: "block",
    height: 3,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.secondary?.main || theme.palette.primary.light, 0.85)})`,
  },
});

/** Tarjeta de contenido con gradiente suave */
export const pageContentCardSx = (
  theme,
  accentColor,
  { highlighted = false } = {},
) => {
  const accent = accentColor || theme.palette.primary.main;
  return {
    width: "100%",
    minWidth: 0,
    p: { xs: 1.5, sm: 2, md: 2.5 },
    borderRadius: 3,
    border: "1px solid",
    borderColor: highlighted
      ? alpha(accent, 0.45)
      : alpha(theme.palette.divider, 0.75),
    background: `linear-gradient(145deg, ${alpha(accent, highlighted ? 0.14 : 0.07)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 58%)`,
    boxShadow: highlighted
      ? `0 10px 36px ${alpha(accent, 0.16)}`
      : theme.palette.mode === "dark"
        ? "0 8px 28px rgba(0,0,0,0.22)"
        : "0 10px 36px rgba(15,23,42,0.06)",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  };
};

export const pageFieldSx = (theme) => ({
  "& .MuiInputBase-root": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.background.paper, 0.92)
        : alpha(theme.palette.background.paper, 0.98),
    borderRadius: 1.5,
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.9 : 0.72),
  },
  "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: alpha(theme.palette.primary.main, 0.45),
  },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
  },
  "& .MuiSelect-icon": {
    color: theme.palette.text.secondary,
  },
});

export const pageSectionTitleSx = {
  fontWeight: 800,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  fontSize: "0.72rem",
  color: "text.secondary",
};

export const pageKpiChipSx = {
  height: 24,
  fontWeight: 700,
  fontSize: "0.68rem",
};

export const pagePrimaryButtonSx = (theme) => ({
  py: 1.15,
  fontWeight: 800,
  borderRadius: 2,
  textTransform: "none",
  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
});

export const pageMobileCardSx = (theme, { active = false, accentColor } = {}) => {
  const accent = accentColor || theme.palette.primary.main;
  return {
    position: "relative",
    overflow: "hidden",
    p: 1.5,
    borderRadius: 2.5,
    border: "1px solid",
    borderColor: active ? alpha(accent, 0.45) : alpha(theme.palette.divider, 0.7),
    bgcolor: alpha(theme.palette.background.paper, 0.92),
    boxShadow: active
      ? `0 6px 20px ${alpha(accent, 0.14)}`
      : theme.palette.mode === "dark"
        ? `0 4px 16px ${alpha(theme.palette.common.black, 0.25)}`
        : `0 4px 16px ${alpha(theme.palette.common.black, 0.05)}`,
    transition: "border-color 0.18s ease, box-shadow 0.18s ease",
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 4,
      bgcolor: active ? accent : alpha(accent, 0.35),
    },
  };
};

export const pageDropZoneSx = (theme, { active = false } = {}) => {
  const accent = theme.palette.primary.main;
  return {
    mt: 1,
    p: 2,
    borderRadius: 2,
    border: active ? `2px dashed ${accent}` : "1px dashed",
    borderColor: active ? accent : alpha(theme.palette.divider, 0.9),
    bgcolor: active
      ? alpha(accent, 0.1)
      : alpha(theme.palette.background.default, 0.55),
    cursor: "pointer",
    outline: "none",
    transition: "border-color 0.15s, background-color 0.15s, box-shadow 0.15s",
    "&:focus-visible": {
      borderColor: accent,
      boxShadow: `0 0 0 3px ${alpha(accent, 0.22)}`,
    },
  };
};

export const pageDataGridSx = (theme) => ({
  border: "none",
  borderRadius: 2,
  bgcolor: alpha(theme.palette.background.paper, 0.5),
  "& .MuiDataGrid-columnHeaders": {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
    bgcolor: alpha(theme.palette.background.default, 0.55),
    borderRadius: "8px 8px 0 0",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 800,
    fontSize: "0.72rem",
    letterSpacing: 0.35,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  "& .MuiDataGrid-row": {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.45)}`,
  },
  "& .MuiDataGrid-row:hover": {
    bgcolor: alpha(theme.palette.primary.main, 0.06),
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "none",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: `1px solid ${alpha(theme.palette.divider, 0.75)}`,
  },
});

export const pageVersionChipSx = (theme) => ({
  height: 26,
  fontWeight: 800,
  fontSize: "0.72rem",
  border: "1px solid",
  borderColor: theme.palette.error.main,
  color: theme.palette.error.main,
  bgcolor: alpha(theme.palette.error.main, 0.08),
});

/** Tarjeta contenedora de gráficos (Recharts, etc.) */
export const pageChartCardSx = (theme, { tall = false } = {}) => ({
  height: tall
    ? { xs: "26rem", md: "min(520px, calc(100vh - 240px))" }
    : { xs: "22rem", sm: "26rem", md: "30rem" },
  minHeight: tall ? 380 : 340,
  borderRadius: 3,
  border: "1px solid",
  borderColor: alpha(theme.palette.divider, 0.75),
  background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 58%)`,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 8px 28px rgba(0,0,0,0.22)"
      : "0 10px 36px rgba(15,23,42,0.06)",
  p: { xs: 1.5, sm: 2 },
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
});

export const pageChartTitleSx = {
  fontWeight: 800,
  fontSize: { xs: "0.95rem", sm: "1.05rem" },
  letterSpacing: 0.15,
  mb: 1.5,
  lineHeight: 1.3,
};

export const pageSectionDividerSx = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  my: { xs: 1.5, sm: 2 },
  "&::before, &::after": {
    content: '""',
    flex: 1,
    height: 1,
    bgcolor: alpha(theme.palette.divider, 0.85),
  },
});
